import styled from "styled-components";
import { Button, IconButton, TextField, Tooltip } from "@mui/material";
import * as React from "react";
import {
    EditModes,
    useTemplateViewEdit,
} from "src/components/providers/template-view-edit-provider";
import { Link as RouteLink, useLocation, useParams } from "react-router-dom";
import type { TemplateDashboard } from "src/components/template/types";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const DeleteButton = styled(IconButton)`
    position: absolute;
    top: 5px;
    right: 4px;
    margin-top: -10px;
    margin-right: -10px;
    background-color: #333333;

    &:hover {
        background-color: #555555;
    }
`;

const CloneButton = styled(IconButton)`
    position: absolute;
    top: 38px;
    right: 4px;
    margin-top: -10px;
    margin-right: -10px;
    background-color: #333333;

    &:hover {
        background-color: #555555;
    }
`;

export const DashboardNavButton = styled(Button).withConfig({
    shouldForwardProp: (prop) => prop !== "active",
})`
    & > h1 {
        font-size: 13px;
        margin: 0;
        padding: 0;
    }
    font-size: 13px;
    min-height: 32px;
    text-transform: none;
    cursor: pointer;
    margin: 12px;
    min-width: 126px;
    color: ${({ theme, active }) =>
        active ? theme.palette.primary.main : theme.palette.text.primary};
    border: 1px solid
        ${({ theme, active }) => (active ? theme.palette.primary.main : "transparent")};
    border-radius: 6px;

    &:hover {
        color: ${({ theme }) => theme.palette.primary.main};
        border: 1px solid
            ${({ theme, active }) => (active ? theme.palette.primary.main : "transparent")};
        background-color: transparent;
    }
`;

const TemplateDashboardTitle = ({
    dashboard,
    dashboardIndex,
}: {
    dashboard: TemplateDashboard;
    dashboardIndex: number;
}) => {
    const { teamId } = useParams();
    const [title, setTitle] = React.useState<string>((dashboard && dashboard.title) || "");
    const { editView, onEditMode } = useTemplateViewEdit();

    const { pathname } = useLocation();
    const pathnameSplit = pathname.split("/");
    const path = pathnameSplit[pathnameSplit.length - 1];

    React.useEffect(() => {
        if (dashboard && dashboard.title) setTitle(dashboard.title);
    }, [dashboard && dashboard.title]);

    const saveTitle = () => {
        if (editView) {
            editView({
                dashboardIndex,
                newDashboard: {
                    title,
                    urlHandle: encodeURI(title.toLowerCase().replace(/ /g, "-")),
                },
                editMode: EditModes.editDashboard,
            });
        }
    };

    if (!onEditMode || path !== dashboard.urlHandle)
        return (
            <DashboardNavButton
                variant="outlined"
                active={path === dashboard.urlHandle}
                component={RouteLink}
                to={
                    teamId
                        ? `/teams/${teamId}/dashboard/${dashboard.urlHandle}`
                        : `/dashboard/${dashboard.urlHandle}`
                }
            >
                {path === dashboard.urlHandle ? <h1>{dashboard.title}</h1> : dashboard.title}
            </DashboardNavButton>
        );

    return (
        <div style={{ position: "relative" }}>
            <TextField
                hiddenLabel
                error={false}
                id="outlined-basic"
                variant="outlined"
                size="small"
                value={title}
                helperText={false}
                style={{ margin: "12px" }}
                onChange={(event) => {
                    setTitle(event.target.value);
                }}
                onKeyDown={(event) => {
                    if (event.type === "keydown" && event.key === "Enter") {
                        saveTitle();
                    }
                }}
                onBlur={() => {
                    saveTitle();
                }}
            />
            <Tooltip title="Remove Dashboard">
                <DeleteButton
                    onClick={() => {
                        if (editView) {
                            editView({
                                dashboardIndex,
                                editMode: EditModes.removeDashboard,
                            });
                        }
                    }}
                    aria-label="remove dashboard"
                    size="small"
                >
                    <DeleteSweepIcon />
                </DeleteButton>
            </Tooltip>
            <Tooltip title="Clone Dashboard">
                <CloneButton
                    onClick={() => {
                        if (editView) {
                            editView({
                                dashboardIndex,
                                editMode: EditModes.cloneDashboard,
                            });
                        }
                    }}
                    aria-label="clone dashboard"
                    size="small"
                >
                    <ContentCopyIcon />
                </CloneButton>
            </Tooltip>
        </div>
    );
};

export default TemplateDashboardTitle;
